import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Space,
  Table,
  Select,
  InputNumber,
  Typography,
  message,
  Modal,
  Card,
  Spin,
  DatePicker
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  InboxOutlined,
  PrinterOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { SupplierSelect } from '../../components/suppliers';
import StockInPrintModal from '../../components/stock-in/StockInPrintModal';
import { useAuthStore } from '../../stores/authStore';
import { useQuery } from 'react-query';
import api from '../../services/api';
import { useResponsive } from '../../hooks/useResponsive';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface StockInItem {
  id?: number;
  medicine_id: number;
  medicine_name?: string;
  medicine_code?: string;
  quantity: number;
  unit?: string;
  unit_price: number;
  lot_number: string;
  expiry_date: any;
  manufacturing_date: any;
  notes?: string;
  total_amount?: number;
}

interface StockInRecord {
  id: number;
  voucher_number?: string;
  supplier_id?: number;
  supplier_name?: string;
  total_items?: number;
  total_amount?: number;
  status?: 'pending' | 'completed' | 'cancelled';
  created_at?: string;
  notes?: string;
  items?: StockInItem[];
  voucher?: {
    id: number;
    voucher_number: string;
    supplier_id?: number;
    supplier_name: string;
    total_amount: number;
    notes: string;
    created_at: string;
    status: string;
  };
}

interface Medicine {
  id: number;
  name: string;
  code: string;
  unit: string;
  current_stock: number;
}

const StockInFormPage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;
  const [form] = Form.useForm();
  const [items, setItems] = useState<StockInItem[]>([]);
  const [itemForm] = Form.useForm();
  const [initialFormValues, setInitialFormValues] = useState<any>(null);
  const [initialItems, setInitialItems] = useState<StockInItem[]>([]);
  const [printModalVisible, setPrintModalVisible] = useState(false);
  const [printData, setPrintData] = useState<any>(null);
  const { user } = useAuthStore();
  const { screenWidth } = useResponsive();

  // Fetch medicines
  const { data: medicines = [] } = useQuery(
    'medicines',
    () => api.get('/api/medicines').then(res => res.data.data),
    {
      staleTime: 10 * 60 * 1000,
    }
  );

  // Fetch detail record for editing
  const { data: editDetailRecord, isLoading: isLoadingDetail } = useQuery<StockInRecord>(
    ['stock-in-edit-detail', id],
    () => api.get(`/api/stock/in/${id}`).then(res => res.data.data),
    {
      enabled: isEditMode && !!id,
      staleTime: 1 * 60 * 1000,
    }
  );

  // Load data when editing
  useEffect(() => {
    if (isEditMode && editDetailRecord && !isLoadingDetail) {
      const voucherData = editDetailRecord.voucher || editDetailRecord;
      const itemsData = editDetailRecord.items || [];
      
      form.setFieldsValue({
        supplier_id: voucherData.supplier_id || null,
        notes: voucherData.notes
      });
      
      const mappedItems = itemsData.map((item: any) => {
        const medicine = medicines.find((m: Medicine) => m.id === item.medicine_id);
        return {
          ...item,
          id: item.id,
          medicine_name: medicine?.name || item.medicine_name,
          medicine_code: medicine?.code || item.medicine_code,
          unit: medicine?.unit || item.unit,
          expiry_date: item.expiry_date ? dayjs(item.expiry_date) : null,
          manufacturing_date: item.manufacturing_date ? dayjs(item.manufacturing_date) : null,
          total_amount: item.quantity * item.unit_price
        };
      });
      
      setItems(mappedItems);
    } else if (!isEditMode) {
      form.resetFields();
      setItems([]);
    }
  }, [isEditMode, editDetailRecord, form, isLoadingDetail, medicines]);

  // Lưu snapshot dữ liệu ban đầu
  useEffect(() => {
    if (!isLoadingDetail) {
      setInitialFormValues(form.getFieldsValue());
      setInitialItems([...items]);
    }
  }, [isLoadingDetail]);

  // Cảnh báo khi đóng trình duyệt nếu có dữ liệu chưa lưu
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty()) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [form, items, initialFormValues, initialItems]);

  // Hàm kiểm tra dữ liệu đã thay đổi chưa
  const isDirty = () => {
    if (!initialFormValues) return false;
    const currentForm = form.getFieldsValue();
    return (
      JSON.stringify(currentForm) !== JSON.stringify(initialFormValues) ||
      JSON.stringify(items) !== JSON.stringify(initialItems)
    );
  };

  const handleAddItem = () => {
    itemForm.validateFields().then((values) => {
      const medicine = medicines.find((m: Medicine) => m.id === values.medicine_id);
      
      if (!medicine) return;

      // Validate dates
      if (values.manufacturing_date && values.expiry_date) {
        if (values.manufacturing_date.isAfter(values.expiry_date, 'day')) {
          message.error('Ngày sản xuất không được sau hạn sử dụng');
          return;
        }
      }

      const newItem: StockInItem = {
        medicine_id: values.medicine_id,
        medicine_name: medicine.name,
        medicine_code: medicine.code,
        quantity: values.quantity,
        unit: medicine.unit,
        unit_price: values.unit_price,
        lot_number: values.lot_number,
        expiry_date: values.expiry_date,
        manufacturing_date: values.manufacturing_date,
        notes: values.notes || '',
        total_amount: values.quantity * values.unit_price
      };

      setItems([...items, newItem]);
      itemForm.resetFields();
    });
  };

  const handleRemoveItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
  };

  const handlePrint = () => {
    if (items.length === 0) {
      message.error('Vui lòng thêm ít nhất một sản phẩm để in phiếu');
      return;
    }

    const formValues = form.getFieldsValue();
    const totalAmount = items.reduce((sum, item) => sum + (item.total_amount || 0), 0);
    
    const printData = {
      voucher: {
        id: editDetailRecord?.id || 0,
        voucher_number: editDetailRecord?.voucher_number || editDetailRecord?.voucher?.voucher_number || 'DRAFT-' + Date.now(),
        supplier_name: formValues.supplier_name || '',
        total_amount: totalAmount,
        status: editDetailRecord?.status || 'pending',
        notes: formValues.notes || '',
        created_at: editDetailRecord?.created_at || new Date().toISOString(),
        created_by: user?.username || 'User',
        created_by_name: user?.full_name || user?.username || 'User'
      },
      items: items.map(item => ({
        id: item.id || 0,
        medicine_id: item.medicine_id,
        medicine_name: item.medicine_name || 'N/A',
        medicine_code: item.medicine_code || 'N/A',
        quantity: item.quantity,
        unit: item.unit || 'N/A',
        unit_price: item.unit_price,
        lot_number: item.lot_number || 'N/A',
        expiry_date: item.expiry_date ? dayjs(item.expiry_date).format('DD/MM/YYYY') : '',
        manufacturing_date: item.manufacturing_date ? dayjs(item.manufacturing_date).format('DD/MM/YYYY') : '',
        total_amount: item.total_amount || (item.quantity * item.unit_price),
        notes: item.notes || ''
      }))
    };

    setPrintData(printData);
    setPrintModalVisible(true);
  };

  const updateItemInTable = (index: number, field: keyof StockInItem, value: any) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    // Recalculate total_amount if quantity or unit_price changed
    if (field === 'quantity' || field === 'unit_price') {
      newItems[index].total_amount = (newItems[index].quantity || 0) * (newItems[index].unit_price || 0);
    }
    
    // Validate dates
    if (field === 'manufacturing_date' || field === 'expiry_date') {
      const manufacturingDate = newItems[index].manufacturing_date;
      const expiryDate = newItems[index].expiry_date;
      if (manufacturingDate && expiryDate && dayjs(manufacturingDate).isAfter(dayjs(expiryDate), 'day')) {
        message.error('Ngày sản xuất không được sau hạn sử dụng');
        return;
      }
    }
    
    setItems(newItems);
  };

  const columns = [
    {
      title: 'STT',
      key: 'index',
      width: 60,
      fixed: 'left' as const,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: 'Tên sản phẩm',
      dataIndex: 'medicine_name',
      key: 'medicine_name',
      width: 200,
      minWidth: 180,
      render: (text: string, record: StockInItem) => {
        const medicine = medicines.find((m: Medicine) => m.id === record.medicine_id);
        return (
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {text || medicine?.name || 'N/A'}
            </div>
            <div style={{ fontSize: '12px', color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Mã: {record.medicine_code || medicine?.code || 'N/A'}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Số lượng',
      key: 'quantity',
      width: 130,
      render: (_: any, record: StockInItem, index: number) => {
        const medicine = medicines.find((m: Medicine) => m.id === record.medicine_id);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <InputNumber
              min={1}
              value={record.quantity || 0}
              onChange={(value) => updateItemInTable(index, 'quantity', value || 1)}
              size="small"
              style={{ flex: 1, minWidth: 0 }}
            />
            <div style={{ fontSize: '12px', color: '#666', whiteSpace: 'nowrap', minWidth: '50px' }}>
              {record.unit || medicine?.unit || 'N/A'}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Đơn giá',
      key: 'unit_price',
      width: 160,
      render: (_: any, record: StockInItem, index: number) => (
        <InputNumber
          min={0}
          value={record.unit_price || 0}
          onChange={(value) => updateItemInTable(index, 'unit_price', value || 0)}
          formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(value: string | undefined) => value ? Number(value.replace(/\$\s?|(,*)/g, '')) : 0}
          size="small"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Thành tiền',
      key: 'total_amount',
      width: 150,
      render: (_: any, record: StockInItem) => (
        <div style={{ textAlign: 'right', fontWeight: 500, padding: '4px 8px', whiteSpace: 'nowrap' }}>
          {(record.total_amount || 0).toLocaleString('vi-VN')}
        </div>
      ),
    },
    {
      title: 'Số lô',
      key: 'lot_number',
      width: 140,
      render: (_: any, record: StockInItem, index: number) => (
        <Input
          value={record.lot_number || ''}
          onChange={(e) => updateItemInTable(index, 'lot_number', e.target.value)}
          placeholder="Số lô"
          size="small"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'HSD',
      key: 'expiry_date',
      width: 140,
      render: (_: any, record: StockInItem, index: number) => (
        <DatePicker
          value={record.expiry_date ? dayjs(record.expiry_date) : null}
          onChange={(date) => updateItemInTable(index, 'expiry_date', date)}
          format="DD/MM/YYYY"
          size="small"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'NSX',
      key: 'manufacturing_date',
      width: 140,
      render: (_: any, record: StockInItem, index: number) => (
        <DatePicker
          value={record.manufacturing_date ? dayjs(record.manufacturing_date) : null}
          onChange={(date) => updateItemInTable(index, 'manufacturing_date', date)}
          format="DD/MM/YYYY"
          size="small"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 90,
      fixed: 'right' as const,
      render: (_: any, __: any, index: number) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveItem(index)}
          size="small"
        />
      ),
    },
  ];

  const totalAmount = items.reduce((sum, item) => sum + (item.total_amount || 0), 0);

  const handleCancel = () => {
    if (isDirty()) {
      Modal.confirm({
        title: 'Bạn có chắc muốn thoát?',
        content: 'Dữ liệu chưa lưu sẽ bị mất. Bạn có muốn tiếp tục?',
        okText: 'Thoát',
        cancelText: 'Ở lại',
        onOk: () => {
          navigate('/stock-in');
        },
      });
    } else {
      navigate('/stock-in');
    }
  };

  const handleSubmit = async (values: any) => {
    if (items.length === 0) {
      message.error('Vui lòng thêm ít nhất một thuốc');
      return;
    }
    
    // Validate dates for each item
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.manufacturing_date && item.expiry_date) {
        if (dayjs(item.manufacturing_date).isAfter(dayjs(item.expiry_date), 'day')) {
          message.error(`Thuốc ${i + 1}: Ngày sản xuất không được sau hạn sử dụng`);
          return;
        }
      }
    }
    
    const submitData = {
      supplier_id: values.supplier_id || null,
      notes: values.notes || '',
      items: items.map(item => ({
        medicine_id: item.medicine_id,
        quantity: item.quantity,
        unit_price: item.unit_price,
        lot_number: item.lot_number,
        expiry_date: item.expiry_date ? dayjs(item.expiry_date).format('YYYY-MM-DD') : null,
        manufacturing_date: item.manufacturing_date ? dayjs(item.manufacturing_date).format('YYYY-MM-DD') : null,
        notes: item.notes || ''
      }))
    };

    try {
      if (isEditMode && id) {
        const recordId = editDetailRecord?.voucher?.id || editDetailRecord?.id || Number(id);
        await api.put(`/api/stock/in/${recordId}`, submitData);
        message.success('Cập nhật phiếu nhập kho thành công!');
      } else {
        await api.post('/api/stock/in', submitData);
        message.success('Tạo phiếu nhập kho thành công!');
      }
      navigate('/stock-in');
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Có lỗi xảy ra');
    }
  };

  return (
    <div style={{ 
      height: '100%',
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden',
      backgroundColor: '#f0f2f5'
    }}>
      {/* Header */}
      <Card 
        style={{ 
          margin: screenWidth >= 1200 ? '16px 16px 0 16px' : '12px 12px 0 12px',
          borderRadius: '8px 8px 0 0',
          flexShrink: 0
        }}
        bodyStyle={{ padding: screenWidth >= 1200 ? '16px 24px' : '12px 16px' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Title level={4} style={{ margin: 0 }}>
              {isEditMode 
                ? `Sửa phiếu nhập kho ${editDetailRecord?.voucher?.voucher_number || editDetailRecord?.voucher_number || ''}`
                : 'Tạo phiếu nhập kho'
              }
            </Title>
            {isLoadingDetail && (
              <Text type="secondary" style={{ fontSize: '14px' }}>
                (Đang tải dữ liệu...)
              </Text>
            )}
          </div>
        </div>
      </Card>

      {/* Content */}
      <div style={{ 
        flex: 1, 
        margin: screenWidth >= 1200 ? '0 16px 16px 16px' : '0 12px 12px 12px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0
      }}>
        <Card 
          style={{ 
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: '0 0 8px 8px',
            height: '100%'
          }}
          bodyStyle={{ 
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            padding: screenWidth >= 1200 ? '24px' : screenWidth >= 992 ? '20px' : '16px',
            height: '100%'
          }}
        >
          <Spin spinning={isLoadingDetail}>
            <Form
              form={form}
              layout="vertical"
              style={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
              onFinish={handleSubmit}
            >
              {/* Two Column Layout */}
              <div style={{ 
                flex: 1, 
                display: 'flex', 
                flexDirection: screenWidth >= 1200 ? 'row' : 'column',
                gap: screenWidth >= 1200 ? '24px' : screenWidth >= 992 ? '20px' : '16px',
                minHeight: 0,
                overflow: 'hidden'
              }}>
                {/* Left Column - Medicine Details */}
                <div style={{ 
                  flex: 1, 
                  display: 'flex', 
                  flexDirection: 'column',
                  minWidth: 0,
                  overflow: 'hidden'
                }}>
                  {/* Add Item Form */}
                  <div style={{ 
                    flexShrink: 0,
                    padding: '16px', 
                    border: 'none',
                    borderRadius: '6px',
                    marginBottom: '16px',
                    backgroundColor: '#fafafa'
                  }}>
                    <Title level={5} style={{ textAlign: 'left' }}>Thêm thuốc</Title>
                    
                    <Form form={itemForm} layout="inline" style={{ display: 'flex', flexWrap: 'nowrap', gap: '8px', alignItems: 'flex-start' }}>
                      <Form.Item
                        name="medicine_id"
                        rules={[{ required: true, message: 'Chọn thuốc' }]}
                        style={{ marginBottom: 0, flex: '1 1 200px', minWidth: 180 }}
                      >
                        <Select
                          placeholder="Chọn thuốc"
                          style={{ width: '100%' }}
                          showSearch
                          filterOption={(input, option: any) => {
                            const children = option?.children;
                            if (typeof children === 'string') {
                              return children.toLowerCase().includes(input.toLowerCase());
                            }
                            return false;
                          }}
                        >
                          {medicines.map((medicine: Medicine) => (
                            <Option key={medicine.id} value={medicine.id}>
                              {medicine.name} ({medicine.code})
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>

                      <Form.Item 
                        name="quantity" 
                        rules={[{ required: true, message: 'Nhập số lượng' }]}
                        style={{ marginBottom: 0, flex: '0 0 100px' }}
                      >
                        <InputNumber
                          placeholder="Số lượng"
                          min={1}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>

                      <Form.Item 
                        name="unit_price" 
                        rules={[{ required: true, message: 'Nhập đơn giá' }]}
                        style={{ marginBottom: 0, flex: '0 0 130px' }}
                      >
                        <InputNumber
                          placeholder="Đơn giá"
                          min={0}
                          formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          parser={(value: string | undefined) => value ? Number(value.replace(/\$\s?|(,*)/g, '')) : 0}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>

                      <Form.Item
                        name="lot_number"
                        rules={[{ required: true, message: 'Nhập số lô' }]}
                        style={{ marginBottom: 0, flex: '0 0 120px' }}
                      >
                        <Input
                          placeholder="Số lô"
                          style={{ width: '100%' }}
                        />
                      </Form.Item>

                      <Form.Item
                        name="expiry_date"
                        rules={[{ required: true, message: 'Chọn hạn sử dụng' }]}
                        style={{ marginBottom: 0, flex: '0 0 140px' }}
                      >
                        <DatePicker
                          placeholder="Hạn sử dụng"
                          format="DD/MM/YYYY"
                          style={{ width: '100%' }}
                        />
                      </Form.Item>

                      <Form.Item
                        name="manufacturing_date"
                        rules={[{ required: true, message: 'Chọn ngày sản xuất' }]}
                        style={{ marginBottom: 0, flex: '0 0 140px' }}
                      >
                        <DatePicker
                          placeholder="Ngày sản xuất"
                          format="DD/MM/YYYY"
                          style={{ width: '100%' }}
                        />
                      </Form.Item>

                      <Form.Item style={{ marginBottom: 0, flex: '0 0 auto' }}>
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={handleAddItem}
                        >
                          Thêm
                        </Button>
                      </Form.Item>
                    </Form>
                  </div>

                  {/* Items Table */}
                  <div style={{ 
                    flex: 1, 
                    overflow: 'hidden', 
                    minHeight: 0,
                    border: 'none',
                    borderRadius: '6px',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    {items.length === 0 ? (
                      <div style={{ 
                        padding: '40px', 
                        textAlign: 'center', 
                        color: '#999',
                        border: 'none',
                        margin: '8px',
                        borderRadius: '6px'
                      }}>
                        <InboxOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                        <div>Chưa có thuốc nào được thêm</div>
                        <div style={{ fontSize: '12px' }}>Vui lòng thêm thuốc từ form bên trên</div>
                      </div>
                    ) : (
                      <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                        <Table
                          columns={columns}
                          dataSource={items}
                          rowKey={(_record, index) => index?.toString() || '0'}
                          pagination={false}
                          size="small"
                          bordered={false}
                          scroll={{ x: 930 }}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Column - Other Information */}
                <div style={{ 
                  width: screenWidth >= 1200 ? '400px' : screenWidth >= 992 ? '350px' : '100%',
                  display: 'flex', 
                  flexDirection: 'column',
                  flexShrink: 0,
                  overflow: 'hidden'
                }}>
                  <div style={{ 
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '16px', 
                    border: 'none',
                    borderRadius: '6px',
                    backgroundColor: '#fafafa',
                    overflow: 'hidden'
                  }}>
                    {/* Supplier Information */}
                    <div style={{ flexShrink: 0 }}>
                      <Form.Item
                        name="supplier_id"
                        label="Nhà cung cấp"
                        rules={[{ required: true, message: 'Vui lòng chọn nhà cung cấp' }]}
                        style={{ marginBottom: '16px' }}
                      >
                        <SupplierSelect
                          placeholder="Chọn nhà cung cấp"
                          style={{ textAlign: 'left', width: '100%' }}
                        />
                      </Form.Item>
                    </div>

                    {/* Summary */}
                    <div style={{ 
                      flexShrink: 0,
                      padding: '16px',
                      border: 'none',
                      borderRadius: '6px',
                      marginBottom: '16px',
                      backgroundColor: '#f0f8ff',
                      textAlign: 'left'
                    }}>
                      <Title level={5} style={{ marginBottom: '12px', fontSize: '16px', textAlign: 'left' }}>Tổng kết</Title>
                      <div style={{ marginBottom: '8px' }}>
                        <Text>Số lượng thuốc: </Text>
                        <Text strong>{items.length}</Text>
                      </div>
                      <div style={{ marginBottom: '8px' }}>
                        <Text>Tổng số lượng: </Text>
                        <Text strong>{items.reduce((sum, item) => sum + (item.quantity || 0), 0)}</Text>
                      </div>
                      <div style={{ height: '1px', backgroundColor: '#e8e8e8', margin: '12px 0' }} />
                      <div style={{ textAlign: 'right' }}>
                        <Text strong style={{ fontSize: '16px', color: '#52c41a' }}>
                          Tổng tiền: {(totalAmount || 0).toLocaleString('vi-VN')} VNĐ
                        </Text>
                      </div>
                    </div>

                    {/* Notes */}
                    <div style={{ 
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: 0,
                      overflow: 'hidden',
                      marginBottom: '70px'
                    }}>
                      <Form.Item 
                        name="notes" 
                        label="Ghi chú"
                        style={{ 
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          marginBottom: 0
                        }}
                      >
                        <TextArea 
                            rows={9} 
                          placeholder="Nhập ghi chú (nếu có)"
                          style={{ 
                            resize: 'none',
                            minHeight: '180px'
                          }}
                        />
                      </Form.Item>
                    </div>

                    {/* Action Buttons */}
                    <div style={{ 
                      flexShrink: 0,
                      textAlign: 'right', 
                      paddingTop: '24px',
                      borderTop: 'none'
                    }}>
                      <Space>
                        <Button onClick={handleCancel}>
                          Hủy
                        </Button>
                        <Button 
                          icon={<PrinterOutlined />} 
                          onClick={handlePrint}
                          disabled={items.length === 0}
                        >
                          In phiếu
                        </Button>
                        <Button type="primary" icon={<SaveOutlined />} htmlType="submit">
                          {isEditMode ? 'Cập nhật' : 'Lưu phiếu'}
                        </Button>
                      </Space>
                    </div>
                  </div>
                </div>
              </div>
            </Form>
          </Spin>
        </Card>
      </div>
      
      {/* Print Modal */}
      <StockInPrintModal
        visible={printModalVisible}
        onCancel={() => setPrintModalVisible(false)}
        data={printData}
      />
    </div>
  );
};

export default StockInFormPage;

